/**
 * Подтверждение личности (§9).
 *
 * Целевой сценарий — полностью внутри Alterium: текст согласия → камера →
 * короткая запись → отправка → ожидание проверки. Внешний redirect-flow
 * допускается как фолбэк, и тогда экран называется нейтрально (§9, §41).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { useRecorder } from '../../features/capture/useRecorder';
import { uploadFile } from '../../features/upload/uploadFile';
import { avatarApi } from '../../api/avatar';
import { useTranslation } from 'react-i18next';

export function ConsentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [progress, setProgress] = useState<number | null>(null);

  const recorder = useRecorder();
  const statement = useQuery({
    queryKey: ['consent-statement'],
    queryFn: avatarApi.consentStatement,
  });

  useEffect(() => {
    const stream = recorder.stream.current;
    if (videoRef.current && stream && recorder.state !== 'recorded') {
      videoRef.current.srcObject = stream;
    }
  }, [recorder.state, recorder.stream]);

  const maxSec = statement.data?.maxSec ?? 30;
  useEffect(() => {
    if (recorder.state === 'recording' && recorder.elapsedSec >= maxSec) recorder.stop();
  }, [recorder, recorder.state, recorder.elapsedSec, maxSec]);

  const submit = useMutation({
    mutationFn: async () => {
      const captured = recorder.captured;
      if (!captured) throw new Error(t('consent.noRecording'));

      const extension = captured.mimeType.includes('mp4') ? 'mp4' : 'webm';
      const file = new File([captured.blob], `consent.${extension}`, {
        type: captured.mimeType.split(';')[0] ?? 'video/webm',
      });

      const asset = await uploadFile(file, {
        kind: 'video',
        onProgress: ({ loaded, total }) => setProgress(Math.round((loaded / total) * 100)),
      });

      await avatarApi.submitConsent(asset.id, statement.data?.version);
    },
    onSuccess: () => {
      recorder.release();
      navigate('/persona', { replace: true });
    },
    onSettled: () => setProgress(null),
  });

  const minSec = statement.data?.minSec ?? 3;
  const tooShort = (recorder.captured?.durationSec ?? 0) < minSec;

  return (
    <div className="flex min-h-full flex-col bg-page">
      <header className="flex items-center justify-between px-5 py-4">
        <span className="text-card-title font-semibold tracking-tight">{t('consent.title')}</span>
        <button
          type="button"
          onClick={() => {
            recorder.release();
            navigate('/persona');
          }}
          aria-label={t('common.close')}
          className="rounded-control p-1.5 text-content-muted transition-colors duration-fast hover:text-content"
        >
          <X className="h-5 w-5" strokeWidth={1.75} />
        </button>
      </header>

      <div className="flex flex-1 flex-col gap-6 px-5 pb-8 lg:flex-row lg:gap-8 lg:px-8">
        <div className="mx-auto w-full max-w-[360px] overflow-hidden rounded-card bg-surface-secondary lg:mx-0">
          <div className="aspect-[9/16] w-full">
            {recorder.state === 'recorded' && recorder.captured ? (
              <video src={recorder.captured.url} controls playsInline className="h-full w-full object-cover" />
            ) : (
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="h-full w-full -scale-x-100 object-cover"
              />
            )}
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-5">
          {recorder.error ? <Alert tone="error">{recorder.error}</Alert> : null}
          {submit.isError ? <Alert tone="error">{(submit.error as Error).message}</Alert> : null}

          <div>
            <h1 className="text-section-title font-semibold">{t('consent.prompt')}</h1>
            <p className="mt-2 text-body text-content-secondary">{t('consent.explanation')}</p>
          </div>

          <blockquote className="rounded-card border border-line bg-surface px-5 py-4 text-[17px] leading-relaxed text-content">
            {statement.data?.text}
          </blockquote>

          <ul className="flex flex-col gap-2">
            {(statement.data?.instructions ?? []).map((item) => (
              <li key={item} className="flex gap-2.5 text-body text-content-secondary">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-content-muted" />
                {item}
              </li>
            ))}
          </ul>

          {progress !== null ? (
            <div className="progress-track">
              <div className="progress-bar" style={{ width: `${progress}%` }} />
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3">
            {recorder.state === 'idle' || recorder.state === 'error' ? (
              <Button variant="primary" onClick={() => void recorder.requestPermission()}>
                {recorder.state === 'error' ? t('common.tryAgain') : t('capture.enableCamera')}
              </Button>
            ) : null}

            {recorder.state === 'ready' ? (
              <Button variant="primary" onClick={() => void recorder.start()}>{t('consent.record')}</Button>
            ) : null}

            {recorder.state === 'recording' ? (
              <Button onClick={recorder.stop} disabled={recorder.elapsedSec < minSec}>
                {t('consent.done', { seconds: Math.floor(recorder.elapsedSec) })}
              </Button>
            ) : null}

            {recorder.state === 'recorded' ? (
              <>
                <Button
                  variant="primary"
                  onClick={() => submit.mutate()}
                  disabled={submit.isPending || tooShort}
                >
                  {submit.isPending ? t('auth.sending') : t('common.send')}
                </Button>
                <Button onClick={recorder.reset} disabled={submit.isPending}>{t('capture.rerecord')}</Button>
              </>
            ) : null}
          </div>

          {recorder.state === 'recorded' && tooShort ? (
            <p className="text-meta text-warning">{t('consent.tooShort')}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
